import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  Database,
  Heart,
  CheckSquare,
  Bell,
  Server,
} from 'lucide-react';
import type { Log, LogType, LogLevel } from '../types';
import clsx from 'clsx';

interface ActivityFeedProps {
  logs: Log[];
}

const typeIcons: Record<LogType, typeof Database> = {
  BACKUP: Database,
  HEALTH: Heart,
  VALIDATOR: CheckSquare,
  NOTIFICATION: Bell,
  SYSTEM: Server,
};

const levelColors: Record<LogLevel, string> = {
  DEBUG: 'text-gray-400',
  INFO: 'text-blue-500',
  WARNING: 'text-yellow-500',
  ERROR: 'text-red-500',
};

export default function ActivityFeed({ logs }: ActivityFeedProps) {
  if (logs.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        Keine Aktivitäten vorhanden
      </div>
    );
  }

  return (
    <div className="space-y-3 max-h-[400px] overflow-y-auto">
      {logs.map((log) => {
        const Icon = typeIcons[log.type] || Server;

        return (
          <div
            key={log.id}
            className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <div
              className={clsx(
                'p-2 rounded-lg',
                log.level === 'ERROR'
                  ? 'bg-red-100'
                  : log.level === 'WARNING'
                  ? 'bg-yellow-100'
                  : 'bg-gray-100'
              )}
            >
              <Icon className={clsx('w-4 h-4', levelColors[log.level])} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-900 truncate">{log.message}</p>
              <div className="flex items-center gap-2 mt-1">
                {log.project && (
                  <span className="text-xs text-primary-600 font-medium">
                    {log.project.name}
                  </span>
                )}
                <span className="text-xs text-gray-500">
                  {formatDistanceToNow(new Date(log.createdAt), {
                    addSuffix: true,
                    locale: de,
                  })}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
